import { AxiosInstance } from "axios";
import { useContext, useEffect, useState } from "react";
import httpClientFactory from "../../config/httpConfig";
import { AppConfig } from "../../context/context";
import { IApplicationModel } from "./applications-model";

const TENANTS_ENDPOINT = "v1/DevTenants";

interface IApplicationService {
  getAllApps: (...args: any[]) => Promise<any>;
  getApp: (name: string, id: string) => Promise<any>;
  createApp: (...args: any[]) => Promise<any>;
  patchApplication: (...args: any[]) => Promise<any>;
  delete: (...args: any[]) => Promise<any>;
  deleteTenantApp: (name: string, id: string) => Promise<any>;
  regenerateKey: (name: string, id: string) => Promise<any>;
  getUsageReport: (...args: any[]) => Promise<any>;
  getUsageMetrics: (name: string, id: string, start: string, end: string) => Promise<any>;
}

export const tenantViewToOrg = (org: any): IApplicationModel => {
  return {
    id: org.uniqueId,
    name: org.applicationName,
    dateAdded: org.applicationCreationDateUtcSeconds,
    clientId: org.clientID,
    accessKey: org.subscriptionKey,
  };
};

export const mapModelToApplicationList = (data: any) => {
  const rows: any[] = [];

  (data.devTenantApps || []).forEach((org: any) => {
    rows.push(tenantViewToOrg(org));
  });

  return {
    devTenantApps: rows,
    count: data.count ?? 0,
  };
};

export const ApplicationService = (config: any, mockHttp?: jest.Mocked<AxiosInstance>): IApplicationService => {
  const http: AxiosInstance = mockHttp || httpClientFactory(config); // <--- use mock if provided
  const adminTenantId = config.tenantId;
  
  return {
    getAllApps: async (
  name: string,
  page: number = 1,
  size: number = 100,
) => {
  return await http
    .get(`${TENANTS_ENDPOINT}/${name}/DevApplications?page=${page}&size=${size}`)
    .then((data: any) => {
      const rows: any[] = [];
      (data.devTenantApps || []).forEach((org: any) => {
        rows.push({
          id: org.uniqueId,
          name: org.applicationName,
          dateAdded: org.applicationCreationDateUtcSeconds,
          clientId: org.clientID,
          accessKey: org.subscriptionKey,
        });
      });
      return {
        devTenantApps: rows,
        count: data.count ?? 0,
      };
    });
},
    getApp: (name, id) => {
      return http
        .get(`${TENANTS_ENDPOINT}/${name}/DevApplications/${id}`);
    },
    createApp: async (orgName: string, application: any) => {
      return await http
        .post(`${TENANTS_ENDPOINT}/${orgName}/DevApplications`, application)
        .then((response: any) => response.data);
    },
    patchApplication: async (name: string, id: string, application: any) => {
      return await http.patch(`${TENANTS_ENDPOINT}/${name}/DevApplications/${id}`, application);
    },
    delete: (orgId: any) => http.delete(`${TENANTS_ENDPOINT}/${orgId}`),
    deleteTenantApp: (name, id) => {
      return http.delete(`${TENANTS_ENDPOINT}/${name}/DevApplications/${id}`);
    },
    regenerateKey: async (name: string, id: string) => {
      return await http.patch(`${TENANTS_ENDPOINT}/${name}/DevApplications/${id}/regenerateKey`);
    },
    getUsageReport: async (
      name: string,
      id: string,
      start: string,
      end: string
    ) => {
      return await http
        .get(`${TENANTS_ENDPOINT}/${name}/DevApplications/${id}/usage?start=${start}&end=${end}`);
    },
    getUsageMetrics: async (
      name: string,
      id: string,
      start: string,
      end: string
    ) => {
      return await http
        .get(`${TENANTS_ENDPOINT}/${name}/DevApplications/${id}/usagemetrics?start=${start}&end=${end}`);
    },
  };
};

export function createServiceFromConfig(config: any) {
  if (!config?.baseUrl || !config?.tenantId || !config?.token) return {};
  return ApplicationService(config);
}

export function useService() {
  const { config } = useContext(AppConfig);
  const [appService, setOrgService] = useState<IApplicationService>(
    {} as IApplicationService
  );

  if (!config) {
    throw new Error("useServices hook must be used inside AppConfig context");
  }

  useEffect(() => {
    const { baseUrl, tenantId, token } = config;
    if (!baseUrl || !tenantId || !token) return;
    setOrgService(ApplicationService({ baseUrl, tenantId, token }));
  }, [config]);

  return {
    appService,
  };
}

export default ApplicationService;
